import * as THREE from "three";

export type RandomSource = () => number;

export function mulberry32(seed: number): RandomSource {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashNoise(x: number, z: number, seed: number): number {
  const value = Math.sin(x * 127.1 + z * 311.7 + seed * 0.013) * 43758.5453123;
  return value - Math.floor(value);
}

export function smoothNoise(x: number, z: number, seed: number): number {
  const x0 = Math.floor(x);
  const z0 = Math.floor(z);
  const tx = x - x0;
  const tz = z - z0;
  const sx = tx * tx * (3 - 2 * tx);
  const sz = tz * tz * (3 - 2 * tz);
  const a = hashNoise(x0, z0, seed);
  const b = hashNoise(x0 + 1, z0, seed);
  const c = hashNoise(x0, z0 + 1, seed);
  const d = hashNoise(x0 + 1, z0 + 1, seed);
  return THREE.MathUtils.lerp(
    THREE.MathUtils.lerp(a, b, sx),
    THREE.MathUtils.lerp(c, d, sx),
    sz,
  );
}

export function fractalNoise(x: number, z: number, seed: number): number {
  let value = 0;
  let amplitude = 0.58;
  let frequency = 0.08;
  let normalizer = 0;

  for (let octave = 0; octave < 4; octave += 1) {
    value += smoothNoise(x * frequency, z * frequency, seed + octave * 37) * amplitude;
    normalizer += amplitude;
    amplitude *= 0.5;
    frequency *= 2.05;
  }

  return value / normalizer;
}

function parseColor(color: string): [number, number, number] {
  const normalized = color.replace("#", "");
  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    const value = Number.parseInt(normalized, 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  }
  const parsed = new THREE.Color(color).convertLinearToSRGB();
  return [parsed.r * 255, parsed.g * 255, parsed.b * 255];
}

export function createPainterlyTexture(
  colors: readonly string[],
  seed: number,
  size = 256,
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("無法建立材質畫布");

  const random = mulberry32(seed);
  const palette = colors.map(parseColor);
  const image = context.createImageData(size, size);

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const broad = fractalNoise(x, y, seed);
      const grain = random() * 0.12 - 0.06;
      const scaled = THREE.MathUtils.clamp(broad + grain, 0, 0.999);
      const palettePosition = scaled * (palette.length - 1);
      const low = Math.floor(palettePosition);
      const high = Math.min(palette.length - 1, low + 1);
      const blend = palettePosition - low;
      const offset = (y * size + x) * 4;
      image.data[offset] = THREE.MathUtils.lerp(palette[low][0], palette[high][0], blend);
      image.data[offset + 1] = THREE.MathUtils.lerp(palette[low][1], palette[high][1], blend);
      image.data[offset + 2] = THREE.MathUtils.lerp(palette[low][2], palette[high][2], blend);
      image.data[offset + 3] = 255;
    }
  }

  context.putImageData(image, 0, 0);
  context.globalCompositeOperation = "soft-light";

  for (let index = 0; index < 440; index += 1) {
    const [red, green, blue] = palette[Math.floor(random() * palette.length)];
    context.strokeStyle = `rgba(${red}, ${green}, ${blue}, ${0.035 + random() * 0.06})`;
    context.lineWidth = 0.5 + random() * 2.4;
    context.beginPath();
    const startX = random() * size;
    const startY = random() * size;
    context.moveTo(startX, startY);
    context.quadraticCurveTo(
      startX + (random() - 0.5) * 24,
      startY + (random() - 0.5) * 16,
      startX + (random() - 0.5) * 48,
      startY + (random() - 0.5) * 24,
    );
    context.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(5, 5);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.needsUpdate = true;
  return texture;
}

export function createCloudTexture(size = 256): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("無法建立雲朵材質");

  const gradient = context.createRadialGradient(
    size * 0.5,
    size * 0.47,
    size * 0.04,
    size * 0.5,
    size * 0.5,
    size * 0.5,
  );
  gradient.addColorStop(0, "rgba(255, 255, 244, 0.96)");
  gradient.addColorStop(0.42, "rgba(255, 252, 232, 0.72)");
  gradient.addColorStop(0.75, "rgba(225, 244, 239, 0.22)");
  gradient.addColorStop(1, "rgba(220, 242, 239, 0)");
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

export function createGlowTexture(
  inner = "rgba(190,255,235,1)",
  outer = "rgba(61,228,207,0)",
  size = 128,
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("無法建立光暈材質");

  const gradient = context.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2,
  );
  gradient.addColorStop(0, inner);
  gradient.addColorStop(0.18, inner);
  gradient.addColorStop(0.55, "rgba(96,245,219,0.26)");
  gradient.addColorStop(1, outer);
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export function randomPointInEllipse(
  random: RandomSource,
  radiusX: number,
  radiusZ: number,
  margin = 0.12,
): THREE.Vector2 {
  const angle = random() * Math.PI * 2;
  const distance = Math.sqrt(random()) * (1 - margin);
  return new THREE.Vector2(
    Math.cos(angle) * radiusX * distance,
    Math.sin(angle) * radiusZ * distance,
  );
}

export function setObjectShadow(object: THREE.Object3D, enabled = true): void {
  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.castShadow = enabled;
      child.receiveShadow = enabled;
    }
  });
}
